import type { ReactElement } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useCart } from '../../hooks/useCart';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { isClient } from '../../utils/utils';

interface Props {
    showSavings?: boolean;
}

export const BasketOrderTotalSummary = ({ showSavings = false }: Props): ReactElement => {
    const { currency, basket = {} } = useCart();
    const dollarSavingsThreshold = useCustomPreferenceOrDefault(
        CustomPreference.SAVINGS_DOLLAR_THRESHOLD,
        10,
    );

    const { orderTotal = 0, c_totalSavings = 0 } = basket;

    const isSavingsEnabled = isClient && window.savingsFeatureMode?.includes('CART_ORDER_SUMMARY');

    const shouldShowSavings =
        showSavings &&
        isSavingsEnabled &&
        c_totalSavings >= dollarSavingsThreshold &&
        c_totalSavings > 0;

    const textStyle = showSavings ? 'body-base-pepper-1000' : 'slt-body-text-lg';
    const footerStyle = showSavings
        ? { textStyle, fontWeight: '500' }
        : {
              textStyle: 'slt-heading-xl',
              fontSize: '1.25rem',
              lineHeight: '1.75rem',
              letterSpacing: '0.015625rem',
          };

    return (
        <Flex gap={1} direction="column">
            <Flex justifyContent="space-between" alignItems="center">
                <Text data-testid="order-summary-order-total-label" {...footerStyle}>
                    <FormattedMessage id="order_summary.order_total" defaultMessage="Order Total" />
                </Text>

                <Text data-testid="order-summary-order-total-value" {...footerStyle}>
                    <FormattedNumber style="currency" currency={currency} value={orderTotal} />
                </Text>
            </Flex>
            {shouldShowSavings ? (
                <Flex justifyContent="space-between">
                    <Text
                        data-testid="order-summary-total-savings-label"
                        textStyle="body-small"
                        color="slt-red.1000"
                        fontWeight={500}
                    >
                        <FormattedMessage id="order_summary.you_save" defaultMessage="You Save" />
                    </Text>

                    <Text
                        data-testid="order-summary-total-savings-value"
                        textStyle="body-small"
                        color="slt-red.1000"
                        fontWeight={500}
                    >
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={c_totalSavings}
                        />
                    </Text>
                </Flex>
            ) : null}
        </Flex>
    );
};

BasketOrderTotalSummary.defaultProps = {
    showSavings: false,
};
